import React from 'react';
import Icon from '../../../components/AppIcon';

const InnovationLab = () => {
  const innovations = [
    {
      id: 1,
      title: "AI-Powered Threat Detection",
      partner: "Microsoft Azure AI",
      status: "Beta Testing",
      description: "Advanced machine learning models for real-time cybersecurity threat identification and automated response protocols.",
      features: ["Real-time Analysis", "Predictive Alerts", "Auto-remediation"],
      availability: "Q2 2026",
      icon: "Brain",
      color: "from-primary to-secondary"
    },
    {
      id: 2,
      title: "Intelligent Workflow Automation",
      partner: "Zoho Flow + Power Automate",
      status: "Early Access",
      description: "Cross-platform automation combining Zoho and Microsoft ecosystems for seamless business process optimization.",
      features: ["Multi-platform Sync", "Smart Triggers", "Custom Logic"],
      availability: "Q3 2026",
      icon: "Zap",
      color: "from-secondary to-accent"
    },
    {
      id: 3,
      title: "Quantum-Safe Encryption",
      partner: "Acronis Cyber Infrastructure",
      status: "Research Phase",
      description: "Next-generation encryption protocols preparing enterprises for quantum computing era with future-proof data protection.",
      features: ["Quantum Resistant", "Zero-Trust Architecture", "Hybrid Encryption"],
      availability: "Q4 2026",
      icon: "Lock",
      color: "from-accent to-primary"
    },
    {
      id: 4,
      title: "Predictive Analytics Platform",
      partner: "Azure Synapse + Zoho Analytics",
      status: "Beta Testing",
      description: "Unified analytics platform combining data warehousing with advanced visualization for actionable business intelligence.",
      features: ["Real-time Insights", "Predictive Models", "Custom Dashboards"],
      availability: "Q1 2026",
      icon: "BarChart3",
      color: "from-primary to-accent"
    },
    {
      id: 5,
      title: "Edge Computing Solutions",
      partner: "Azure IoT + Acronis Edge",
      status: "Pilot Program",
      description: "Distributed computing architecture enabling low-latency processing and enhanced security for IoT deployments.",
      features: ["Edge Processing", "Local Storage", "Secure Sync"],
      availability: "Q2 2026",
      icon: "Cpu",
      color: "from-secondary to-primary"
    },
    {
      id: 6,
      title: "Blockchain Integration Framework",
      partner: "Multi-Partner Collaboration",
      status: "Concept Development",
      description: "Enterprise blockchain solutions for supply chain transparency, smart contracts, and decentralized data management.",
      features: ["Smart Contracts", "Immutable Ledger", "Multi-chain Support"],
      availability: "Q4 2026",
      icon: "Link",
      color: "from-accent to-secondary"
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case "Beta Testing":
        return "bg-accent/10 text-accent";
      case "Early Access":
        return "bg-primary/10 text-primary";
      case "Pilot Program":
        return "bg-secondary/10 text-secondary";
      default:
        return "bg-muted text-text-secondary";
    }
  };

  return (
    <section className="py-12 md:py-16 lg:py-20 px-4 md:px-6 lg:px-8 bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 md:mb-16 lg:mb-20">
          <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
            <Icon name="Rocket" size={18} color="var(--color-primary)" />
            <span className="text-primary text-sm font-medium">Innovation Lab</span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Emerging Technology Partnerships
          </h2>
          <p className="text-text-secondary text-sm md:text-base lg:text-lg max-w-3xl mx-auto">
            Pioneering next-generation solutions through collaborative research and development with technology partners, preparing enterprises for tomorrow's digital landscape.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {innovations?.map((innovation) => (
            <div
              key={innovation?.id}
              className="bg-card rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group"
            >
              <div className={`h-2 bg-gradient-to-r ${innovation?.color}`}></div>
              
              <div className="p-6 md:p-8">
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${innovation?.color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon name={innovation?.icon} size={28} color="#ffffff" />
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(innovation?.status)}`}>
                    {innovation?.status}
                  </span>
                </div>

                <h3 className="text-lg md:text-xl font-bold text-foreground mb-2">
                  {innovation?.title}
                </h3>

                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Users" size={14} color="var(--color-text-secondary)" />
                  <span className="text-xs text-text-secondary">{innovation?.partner}</span>
                </div>

                <p className="text-sm text-text-secondary mb-6 line-clamp-3">
                  {innovation?.description}
                </p>

                <div className="space-y-3 mb-6">
                  {innovation?.features?.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0"></div>
                      <span className="text-sm text-foreground">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="pt-6 border-t border-border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon name="Calendar" size={16} color="var(--color-secondary)" />
                      <span className="text-xs text-text-secondary">Expected</span>
                    </div>
                    <span className="text-sm font-semibold text-foreground">{innovation?.availability}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 md:mt-16 bg-card rounded-2xl p-6 md:p-8 lg:p-12 shadow-lg">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-accent/10 px-4 py-2 rounded-full mb-6">
                <Icon name="Sparkles" size={18} color="var(--color-accent)" />
                <span className="text-accent text-sm font-medium">Beta Access Program</span>
              </div>
              <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground mb-4">
                Join Our Innovation Partners
              </h3>
              <p className="text-sm md:text-base text-text-secondary mb-6">
                Get early access to cutting-edge solutions, participate in beta testing programs, and shape the future of enterprise technology with exclusive partnership opportunities.
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Icon name="CheckCircle2" size={20} color="var(--color-accent)" />
                  <span className="text-sm text-foreground">Priority access to new integrations</span>
                </div>
                <div className="flex items-center gap-3">
                  <Icon name="CheckCircle2" size={20} color="var(--color-accent)" />
                  <span className="text-sm text-foreground">Dedicated technical support team</span>
                </div>
                <div className="flex items-center gap-3">
                  <Icon name="CheckCircle2" size={20} color="var(--color-accent)" />
                  <span className="text-sm text-foreground">Influence product development roadmap</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 md:gap-6">
              <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl p-6 text-center">
                <Icon name="Rocket" size={32} color="var(--color-primary)" className="mx-auto mb-3" />
                <div className="text-2xl md:text-3xl font-bold text-foreground mb-1">6</div>
                <div className="text-xs text-text-secondary">Active Projects</div>
              </div>
              <div className="bg-gradient-to-br from-secondary/10 to-accent/10 rounded-2xl p-6 text-center">
                <Icon name="Users" size={32} color="var(--color-secondary)" className="mx-auto mb-3" />
                <div className="text-2xl md:text-3xl font-bold text-foreground mb-1">15+</div>
                <div className="text-xs text-text-secondary">Partner Teams</div>
              </div>
              <div className="bg-gradient-to-br from-accent/10 to-primary/10 rounded-2xl p-6 text-center">
                <Icon name="TrendingUp" size={32} color="var(--color-accent)" className="mx-auto mb-3" />
                <div className="text-2xl md:text-3xl font-bold text-foreground mb-1">2026</div>
                <div className="text-xs text-text-secondary">Launch Timeline</div>
              </div>
              <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-2xl p-6 text-center">
                <Icon name="Award" size={32} color="var(--color-primary)" className="mx-auto mb-3" />
                <div className="text-2xl md:text-3xl font-bold text-foreground mb-1">100%</div>
                <div className="text-xs text-text-secondary">Innovation Focus</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InnovationLab;